/**
 * himali-fixes.js
 *
 * Behavioural corrections layered on top of Feather. Loaded with `defer` from
 * layout/theme.liquid. Nothing here edits a Feather file, so the theme
 * developer's future updates stay safe to apply.
 *
 * Two fixes:
 *   1. Open the cart drawer when something is added to the cart.
 *   2. Stop the announcement bar auto-rotating under prefers-reduced-motion.
 */

(function () {
  'use strict';

  /* -------------------------------------------------------------------------
   * 1. Open the cart drawer on add-to-cart.
   *
   * The drawer itself is fine: `cart_type` is already "drawer",
   * `theme-drawer#cart-drawer` exists, and calling
   * `Shopify.actions.openCart()` by hand opens it correctly. The gap is that
   * nothing invokes it after an add, so the only confirmation a customer gets
   * is the cart badge in the far corner of the header incrementing.
   *
   * The theme dispatches `shopify:cart:lines-update` on the
   * <product-form-component> that submitted. The listener is registered in the
   * CAPTURE phase on document, because the capture phase reaches ancestors for
   * every dispatched event regardless of whether it bubbles.
   * ---------------------------------------------------------------------- */

  function cartDialog() {
    return document.querySelector('theme-drawer#cart-drawer dialog');
  }

  function onCartLinesUpdate(event) {
    var target = event.target;

    // Only adds originating from a product form. Quantity edits made inside
    // the drawer or on the cart page dispatch from elsewhere and must not
    // re-trigger the drawer.
    if (!target || target.tagName !== 'PRODUCT-FORM-COMPONENT') return;

    // On the cart page the drawer would duplicate what is already on screen.
    if (/\/cart(\/|$)/.test(window.location.pathname)) return;

    var actions = window.Shopify && window.Shopify.actions;
    if (!actions || typeof actions.openCart !== 'function') return;

    // Already open (e.g. a second add) — leave it alone rather than re-opening.
    var dialog = cartDialog();
    if (dialog && dialog.hasAttribute('open')) return;

    try {
      actions.openCart();
    } catch (error) {
      // Never let a presentation fix break the add itself. The item is already
      // in the cart by this point; failing silently leaves the previous
      // behaviour rather than throwing in the customer's face.
    }
  }

  document.addEventListener('shopify:cart:lines-update', onCartLinesUpdate, true);

  /* -------------------------------------------------------------------------
   * 2. Respect prefers-reduced-motion on the announcement bar.
   *
   * <announcement-bar-component autoplay="5"> rotates on a JS interval that
   * never consults the media query, so it keeps moving for users who have
   * asked the OS for less motion. It also carries aria-live="polite", which
   * means every rotation is announced to a screen reader, indefinitely, for
   * content that is marketing rather than a live status.
   *
   * The component gates play() on the `autoplay` attribute, so removing that
   * attribute stops it for good — pause() alone would be undone by the
   * component's own mouseleave/resume handler.
   *
   * The theme already ships reduced-motion blocks in three other sections, so
   * this follows an established intent rather than introducing a new policy.
   * ---------------------------------------------------------------------- */

  function calmAnnouncementBars() {
    var query = window.matchMedia('(prefers-reduced-motion: reduce)');
    var bars = document.querySelectorAll('announcement-bar-component[autoplay]');

    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];

      // Rotating marketing copy is not a live region. Silence it whether or not
      // reduced motion is requested; the manual prev/next controls remain.
      if (bar.getAttribute('aria-live')) bar.setAttribute('aria-live', 'off');

      if (!query.matches) continue;

      if (typeof bar.pause === 'function') bar.pause();
      bar.removeAttribute('autoplay');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', calmAnnouncementBars, { once: true });
  } else {
    calmAnnouncementBars();
  }
})();
